<template>
  <JoszakiPage
    :inner-class-object="{
      'grid gap-5 w-full items-start grid-cols-1 md:grid-cols-[300px_1fr]':
        person,
      'lg:grid-cols-[300px_1fr]': true,
    }"
    :tracking-params="trackingParams"
    :pageview-ready="trackingReady"
    :page-name="pageName"
  >
    <BreadCrumbs
      v-if="getBreadCrumbs.length > 0"
      :links="getBreadCrumbs"
      class="md:col-span-2"
    />
    <JoszakiLoading
      v-if="personLoading"
      :active="personLoading"
      :is-full-page="true"
    />
    <template v-else-if="person">
      {{/*Eredeti adatlap*/}}
      <div class="sticky-col">
        <div
          v-if="person.isActive"
          class="text-white bg-success font-bold rounded-md w-fit h-12 -mb-3 pb-2 px-2 self-center flex flex-row items-center gap-2 mx-auto"
        >
          <IconComponent icon="circle" class="-mr-1 inline blink" />
          <span class="uppercase">
            {{ $t("personProfile.openToOffers") }}
          </span>
        </div>
        <CardProfilePerson
          v-if="person"
          :person="person"
          @requestTender="onRequestTender({ source: 'profileCard' })"
        />
        <ProfileInteraction
          v-if="person && !$branding.isSuperprofikCz"
          :person="person"
          class="profile-interaction"
        />
      </div>

      {{/* Original profile body */}}
      <div class="profile-body">
        <div v-if="person.labels" class="flex justify-between items-center">
          <div class="flex flex-row gap-2 flex-wrap">
            <PersonBadge
              v-for="badge in person.labels"
              :key="badge.name"
              :name="badge.name"
              :description="badge.description"
              class="font-extrabold !text-base mr-2"
            />
          </div>
          <FavouriteButton
            :entity-type="FAVOURITE_ENTITY_TYPES.PROFESSIONAL"
            :entity-id="person.id"
            button-class="!text-xl"
          />
        </div>

        <!-- Memo -->
        <section class="profile-section">
          <JoszakiHeading>
            {{ title }}
          </JoszakiHeading>
          <p v-if="topRankedProfessions?.length" class="font-bold">
            <IconComponent icon="briefcase" class="inline mr-1" />
            <template v-for="(profession, index) in topRankedProfessions">
              {{ profession.name }}
              {{ index !== topRankedProfessions.length - 1 ? " · " : "" }}
            </template>
          </p>
          <div
            class="section-card card"
            :class="{ limitedHeight: !memoExpanded }"
          >
            <div ref="personMemo" class="memo p-6">
              <JoszakiHtmlTextBlock
                v-if="person.memoHtml"
                :text="person.memoHtml"
              />
              <span v-else>{{ person.memo }}</span>
            </div>
            <div
              v-if="!memoExpanded"
              class="showMore flex flex-row items-center gap-2"
              @click="memoExpanded = true"
            >
              {{ $t("personProfile.memo.showMore") }}
              <IconComponent icon="plus" />
            </div>
          </div>
        </section>

        <StickyPanels
          :person="person"
          class="sticky max-md:hidden"
          @requestTender="onRequestTender({ source: 'sticky' })"
        />

        <section
          v-if="person.prices && person.prices.length"
          class="profile-section"
        >
          <JoszakiHeading :level="2">
            {{ $t("personProfile.prices.title") }}
          </JoszakiHeading>
          <CardProfilePrices class="section-card" />
          <div class="section-subtext">
            <i18n path="personProfile.prices.accuratePrices">
              <template #offerLink>
                <a @click="onRequestTender({ source: 'prices' })">
                  {{ $t("personProfile.prices.requestOffer") }}
                </a>
              </template>
            </i18n>
          </div>
        </section>

        <!-- Photos -->
        <section v-if="person.photos?.length" class="profile-section">
          <JoszakiHeading :level="2">
            {{ $t("personProfile.references.title") }}
          </JoszakiHeading>
          <PersonReferences :photos="person.photos" class="section-card" />
          <div class="section-subtext">
            <i18n path="personProfile.references.likeReferences">
              <template #offerLink>
                <a @click="onRequestTender({ source: 'photos' })">
                  {{ $t("personProfile.references.requestOffer") }}
                </a>
              </template>
            </i18n>
          </div>
        </section>

        <!-- Scores -->
        <section
          v-if="person.scores?.length"
          id="ratings"
          class="profile-section"
        >
          <JoszakiHeading :level="2">
            {{ $t("personProfile.reviews.title") }}
          </JoszakiHeading>
          <CardScore
            v-for="(score, index) in activeProfessionScores"
            :key="`active-${index}`"
            :score="score"
            class="section-card"
          />
          <div v-if="inactiveProfessionScores.length" class="items-center flex">
            <span class="flex-grow border-t border-gray-400" />
            <span class="flex-shrink mx-2 text-gray-400">
              {{ $t("personProfile.reviews.inactiveProfessions") }}
            </span>
            <span class="flex-grow border-t border-gray-400" />
          </div>
          <CardScore
            v-for="(score, index) in inactiveProfessionScores"
            :key="`inactive-${index}`"
            :score="score"
            class="section-card !opacity-50"
          />
          <template v-if="isAdmin && privateData?.deletedScores?.length">
            <CardScore
              v-for="(score, index) in privateData.deletedScores"
              :key="`deleted-${index}`"
              :score="score"
              class="section-card !opacity-50"
            />
          </template>
          <div class="section-subtext">
            <i18n path="personProfile.reviews.becomeReviewer">
              <template #offerLink>
                <a @click="onRequestTender({ source: 'reviews' })">
                  {{ $t("personProfile.reviews.requestOffer") }}
                </a>
              </template>
            </i18n>
          </div>
        </section>

        <!-- Answers -->
        <section v-if="person.topAnswers?.length" class="profile-section">
          <JoszakiHeading :level="2">
            {{ $t("personProfile.answers.title") }}
          </JoszakiHeading>
          <AnswerInset
            v-for="answer in person.topAnswers"
            :key="answer.id"
            :answer="answer"
            :is-own-profile="true"
          />
        </section>
        <!--</template> -->

        <!-- Professions -->
        <PersonProfessionTags class="profile-section" />

        <PersonProfessionRelatedBlogs :person="person" class="profile-section" />
      </div>

      <StickyPanels
        :person="person"
        class="sticky-mobile"
        @requestTender="onRequestTender({ source: 'sticky' })"
      />

      <ReportPersonModal />
    </template>
  </JoszakiPage>
</template>

<router>
{
  name: 'professionalPage'
}
</router>

<script>
import { mapState } from "pinia";
import { useAsync, useContext, useRoute } from "@nuxtjs/composition-api";
import REDIRECT from "~/stores/admin/queries/redirect.graphql"; // TODO: remove once redirect rework is merged
import { permanentRedirect } from "~/helpers/redirect";
import { parseHashParams } from "~/helpers/generic";
import NunitoVariableLatin from "~/assets/fonts/nunito-variable-latin.woff2";
import NunitoVariableLatinExt from "~/assets/fonts/nunito-variable-latin-ext.woff2";

import { generatePreloadLinks } from "~/helpers/files";

import AnswerInset from "~/components/_refactored/profile/AnswerInset.vue";
import PersonBadge from "~/components/_refactored/profile/PersonBadge.vue";
import CardProfilePrices from "~/components/_refactored/profile/CardProfilePrices.vue";
import CardProfilePerson from "~/components/profile/CardProfilePerson.vue";
import PersonReferences from "~/components/profile/PersonReferences.vue";
import ProfileInteraction from "~/components/profile/ProfileInteraction.vue";
import ReportPersonModal from "~/components/profile/ReportPersonModal.vue";
import CardScore from "~/components/profile/CardScore.vue";
import StickyPanels from "~/components/profile/StickyPanels.vue";

import { useRequestTender } from "~/composables/requestTender";
import { useProfessionalStore } from "~/stores/professional";
import { useAdminProfessionalStore } from "~/stores/admin/professional";
import { useUserStore } from "~/stores/user";
import { EVENT_PAGE_NAMES } from "~/data/pageNames";
import { useNewTender } from "~/composables/tender/useNewTender";
import IS_PHONE_MISMATCH from "~/components/_refactored/card/isPhoneMismatch.graphql";
import PersonProfessionTags from "~/components/profile/PersonProfessionTags.vue";
import BreadCrumbs from "~/components/_refactored/BreadCrumbs.vue";
import PersonProfessionRelatedBlogs from "~/components/profile/PersonProfessionRelatedBlogs.vue";
import JoszakiHtmlTextBlock from "~/components/_refactored/common/JoszakiHtmlTextBlock.vue";
import FavouriteButton from "~/components/_refactored/favourite/FavouriteButton.vue";
import { FAVOURITE_ENTITY_TYPES } from "~/composables/favourite/favouriteEntityTypes";

export default {
  name: "ProfessionalPage",
  components: {
    AnswerInset,
    CardProfilePerson,
    PersonBadge,
    StickyPanels,
    PersonReferences,
    CardScore,
    ProfileInteraction,
    CardProfilePrices,
    ReportPersonModal,
    PersonProfessionTags,
    BreadCrumbs,
    PersonProfessionRelatedBlogs,
    JoszakiHtmlTextBlock,
    FavouriteButton,
  },
  layout: "AdminLayout",
  setup() {
    try {
      const professionalStore = useProfessionalStore();
      const newTender = useNewTender();

      useAsync(async () => {
        const route = useRoute();
        newTender.hideFloater();

        const seoName = route.value.params.professionalSeoName;

        // TODO: remove once redirect rework is merged
        const { redirect, $query, localePath, res } = useContext();
        const { redirect: redirectedSeoName } = await $query(REDIRECT, {
          seoName,
        });
        if (redirectedSeoName && redirectedSeoName !== seoName) {
          return permanentRedirect({
            res,
            redirect,
            location: localePath({
              name: "professionalPage",
              params: { professionalSeoName: redirectedSeoName },
            }),
          });
        }

        await professionalStore.loadPerson(seoName);

        if (!professionalStore.person) {
          permanentRedirect({ res, redirect, location: "/" });
        }
      });
      return {
        pageName: EVENT_PAGE_NAMES.PROFESSIONAL_PAGE,
        professionalStore,
        newTender,
        adminProfessionalStore: useAdminProfessionalStore(),
        userStore: useUserStore(),
        ...useRequestTender(),
        FAVOURITE_ENTITY_TYPES,
      };
    } catch (err) {
      console.error(err);
      return {};
    }
  },
  data() {
    return {
      memoExpanded: false,
      trackingParams: null,
      // Gates the JoszakiPage pageview until the hash-derived highlighted
      // context has been loaded into trackingParams (see mounted()).
      trackingReady: false,
    };
  },
  head() {
    if (!this.person) {
      return;
    }
    const description =
      this.person.memo && this.person.memo.length > 128
        ? `${this.person.memo.slice(0, 125)}...`
        : this.person.memo ?? "";
    const head = {
      title: this.seoTitle,
      script: [
        {
          type: "application/ld+json",
          // Escape "<" so a review body cannot break out of the script tag.
          innerHTML: JSON.stringify(this.jsonld).replace(/</g, "\\u003c"),
        },
      ],
      meta: [
        {
          hid: "og:title",
          property: "og:title",
          content: this.seoTitle,
        },
        {
          hid: "og:url",
          property: "og:url",
          content: `${this.$helpers.topDomain}${this.$route.path}`,
        },
        {
          hid: "og:image",
          property: "og:image",
          content: this.$helpers.getProfilePictureThumbnailUrl(
            this.person.profilePhotoId
          ),
        },
        {
          hid: "description",
          name: "description",
          content: description,
        },
        {
          hid: "og:description",
          property: "og:description",
          content: description,
        },
      ],
      link: [
        {
          rel: "canonical",
          href: process.env.TOP_DOMAIN + this.$route.path,
        },
        ...generatePreloadLinks([NunitoVariableLatin, NunitoVariableLatinExt]),
      ],
    };

    if (this.person.excluded) {
      head.meta.push({
        hid: "robots",
        name: "robots",
        content: "noindex",
      });
    }

    return head;
  },
  computed: {
    ...mapState(useProfessionalStore, {
      person: "person",
      personLoading: "personLoading",
      city: "city",
      profession: "profession",
      rankedProfessions: "rankedProfessions",
      activeProfessionScores: "activeProfessionScores",
      inactiveProfessionScores: "inactiveProfessionScores",
    }),
    ...mapState(useAdminProfessionalStore, {
      privateData: "privateData",
    }),
    // Null/orphaned entries can appear in the professions array (e.g. a
    // personProfession whose profession was deleted, serialized as null).
    // Reading `.name`/`.seoName` on those crashed SSR, so filter them out once
    // here and use this everywhere instead of `this.person.professions`.
    professions() {
      return this.person?.professions?.filter(Boolean) ?? [];
    },
    title() {
      if (!this.person) {
        return "";
      }
      return `${this.person.name} - ${this.professions
        .map((p) => p.name.charAt(0) + p.name.slice(1))
        .sort()
        .join(", ")}`;
    },
    seoTitle() {
      if (!this.person) {
        return "";
      }

      // Show "Name (CompanyName)" if both available, otherwise just the one that exists
      let displayName;
      if (this.person.name && this.person.accountName && this.person.accountName !== this.person.name) {
        displayName = `${this.person.name} (${this.person.accountName})`;
      } else {
        displayName = this.person.name || this.person.accountName;
      }

      // Build professions string
      const professions = this.professions
        .map((p) => p.name.charAt(0) + p.name.slice(1))
        .sort()
        .join(", ");

      // Build location string based on goToHouse settings
      let location;
      if (this.person.goToHouseKm > 500) {
        // Nationwide
        location = this.$t("personProfile.pageTitle.locationNationwide");
      } else if (this.person.goToHouse) {
        // City + radius
        location = this.$t("personProfile.pageTitle.locationWithRadius", {
          city: this.person.city?.name,
          km: this.person.goToHouseKm,
        });
      } else {
        // City only
        location = this.$t("personProfile.pageTitle.locationCity", {
          city: this.person.city?.name,
        });
      }

      // Build the full title using the template
      return this.$t("personProfile.pageTitle.template", {
        name: displayName,
        professions,
        location,
      });
    },
    jsonld() {
      const baseData = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        address: {
          "@type": "PostalAddress",
          addressLocality: this.city?.name,
          addressCountry: "HU",
        },
        name:
          this.person.name.replace(/["'/<>]/gi, "") +
          " - " +
          this.professions.map((p) => p.name).join(", "),
        telephone: this.$helpers.formatPhoneNumber(
          this.person.publicPhoneNumber
        ),
        url: this.$router.fullPath,
        image: this.imageSrc,
      };

      // Only add rating data if there are reviews
      if (this.scoreCount > 0) {
        baseData.aggregateRating = {
          "@type": "AggregateRating",
          ratingValue: Math.max(1, this.person.score / 2),
          reviewCount: this.scoreCount,
        };

        // Add individual reviews
        const activeScores = this.person.scores.filter((s) => !s.deleted);
        if (activeScores.length > 0) {
          baseData.review = activeScores.map((score) => ({
            "@type": "Review",
            author: {
              "@type": "Person",
              name: score.customer?.name || this.$t("personProfile.reviews.anonymous"),
            },
            datePublished: score.created,
            reviewBody: score.what,
            reviewRating: {
              "@type": "Rating",
              ratingValue: Math.max(1, score.score / 2),
              bestRating: 5,
              worstRating: 1,
            },
          }));
        }
      }

      return baseData;
    },
    isAdmin() {
      return this.userStore.isAdmin;
    },
    topRankedProfessions() {
      return this.rankedProfessions?.filter(Boolean).slice(0, 3) ?? [];
    },
    mainProfession() {
      return this.rankedProfessions?.[0];
    },
    getBreadCrumbs() {
      const links = [];
      if (this.profession?.name) {
        if (this.profession.professionGroup) {
          const professionsPagePath = this.localePath({
            name: "professionsLandingPage",
            hash: `#${this.profession.professionGroup.seoName}`,
          });
          links.push({
            name: this.profession.professionGroup.name,
            page: professionsPagePath,
          });
        }
        const professionPagePath = this.localePath({
          name: "professionPage",
          params: {
            profession: this.profession.seoName,
          },
        });
        links.push({
          name: this.profession.name,
          page: professionPagePath,
        });
        links.push({
          name: this.person.name,
          link: "",
        });
      }
      return links;
    },
    scoreCount() {
      return this.person.scores.filter((x) => !x.deleted).length;
    },
    imageSrc() {
      if (!this.person.profilePhotoId) {
        return this.$helpers.noImage;
      }
      return this.$helpers.getProfilePictureThumbnailUrl(
        this.person.profilePhotoId
      );
    },
  },
  watch: {
    isAdmin(newVal, oldVal) {
      if (newVal && !oldVal) {
        this.adminProfessionalStore.loadScoreInfo();
      }
    },
  },
  beforeMount() {
    this.buildTrackingParams();
  },
  async mounted() {
    // Redirect from query params to hash params
    this.redirectQueryToHash();

    try {
      // The highlighted city/profession live in the URL hash, which SSR can't
      // see, so load them now (client-side) before anything reads them below.
      await this.loadHighlightFromHash();
    } finally {
      // Always let JoszakiPage emit the (deferred) pageview — if loading the
      // highlighted context failed it just won't include that context, rather
      // than the pageview never firing at all.
      this.trackingReady = true;
    }

    if (this.person && this.$refs.personMemo) {
      const clientRect = this.$refs.personMemo.getBoundingClientRect();

      this.memoExpanded = clientRect.height < 400;
      this.$cookie.remove("open-profile");

      this.newTender.prefill({
        profession: this.profession,
        city: this.city,
        mate: this.person,
      });

      this.trackViewItem();

      this.seoTrackProfileView();

      if (this.person.publicPhoneNumber?.number) {
        this.$query(IS_PHONE_MISMATCH, {
          personId: this.person.id,
          phone: this.person.publicPhoneNumber.number,
        })
          .then((res) => {
            if (res.isPhoneMismatch) {
              this.$sentry.captureMessage(
                `Phone mismatch for person ${this.person.id}, phone: ${this.person.publicPhoneNumber.number} url: ${window.location.href}`,
                {
                  level: "fatal",
                }
              );
            }
          })
          .catch((err) => {
            console.error(err, "Failed to query phone mismatch");
          });
      }
    }

    if (this.person && this.isAdmin) {
      this.adminProfessionalStore.loadPrivateData();
    }
  },
  methods: {
    redirectQueryToHash() {
      const { city, profession, professionTask } = this.$route.query;

      // If there are relevant query params, redirect to hash version
      if (city || profession || professionTask) {
        const hashParams = [];
        if (city) hashParams.push(`city=${city}`);
        if (profession) hashParams.push(`profession=${profession}`);
        if (professionTask) hashParams.push(`professionTask=${professionTask}`);

        const newUrl = `${this.$route.path}#${hashParams.join("&")}`;
        window.history.replaceState({}, "", newUrl);
      }
    },
    buildTrackingParams() {
      this.trackingParams = {
        personId: this.person?.id,
        personSeoName: this.person?.seoName,
        urlSeoName: this.$route.params.professionalSeoName,
        hasMemo: !!this.person?.memo,
        professionSeoName: this.profession?.seoName,
        professionGroupSeoName: this.profession?.professionGroup?.seoName,
        citySeoName: this.city?.seoName,
      };
    },
    async loadHighlightFromHash() {
      if (!this.person) {
        return;
      }
      const { city, profession, professionTask } = parseHashParams(
        window.location.hash
      );
      await this.professionalStore.loadHighlightedContext({
        citySeoName: city,
        professionSeoName: profession,
        professionTaskSeoName: professionTask,
      });
      // Refresh the snapshot taken in beforeMount, now that the highlighted
      // city/profession are available.
      this.buildTrackingParams();
    },
    trackViewItem() {
      if (!process.server) {
        this.$track.customEvent({ ecommerce: null });
        this.$track.customEvent({
          event: "view_item",
          source_page: "profile",
          city_id: this.city?.Id,
          profession_id: this.profession?.id,
          ecommerce: {
            items: [
              {
                index: 0,
                item_id: this.person?.id,
                item_name: this.person?.name,
                item_list_name: `${this.profession?.name} ${this.city?.name}`,
                item_list_id: `${this.city?.seoName}#${this.profession?.seoName}`,
              },
            ],
          },
        });
      }
    },
    seoTrackProfileView() {
      this.$track.customEvent({
        event: "profileView",
        profession: this.mainProfession?.seoName, // elsődleges szakma
        location: this.person.city.seoName, // település
        registrationDate: this.person.created,
        lastLogin: this.person.lastLogin,
        descWordCount: this.person.memo?.length
          ? this.person.memo.split(" ").length
          : 0, // leírás hossza szavakban
        descCharCount: this.person.memo?.length || 0, // leírás hossza karakterekben
        orders: this.person.winCount, // megbízások száma
        reviews: this.person.scores.length, // vélemények száma
        score: this.person.score / 2, // értékelés átlaga
        hasPhoto: this.$store.getters["professional/hasPicture"], // boolean -> 1 = van fotó, 0 = nincs fotó
      });
    },
    seoTrackProfileTender() {
      this.$track.customEvent({
        event: "beginRequestProposal",
        profession: this.mainProfession?.seoName, // elsődleges szakma
        allProfessions: this.professions.map((x) => x.seoName).join(","),
        location: this.person.city.seoName, // település
        registrationDate: this.person.created,
        lastLogin: this.person.lastLogin,
        descWordCount: this.person.memo?.length
          ? this.person.memo.split(" ").length
          : 0, // leírás hossza szavakban
        descCharCount: this.person.memo?.length || 0, // leírás hossza karakterekben
        orders: this.person.winCount, // megbízások száma
        reviews: this.person.scores.length, // vélemények száma
        score: this.person.score / 2, // értékelés átlaga
        hasPhoto: this.$store.getters["professional/hasPicture"], // boolean -> 1 = van fotó, 0 = nincs fotó
      });
    },
    seoTrackQuickCall() {
      this.$track.customEvent({
        event: "clickCall",
        profession: this.mainProfession?.seoName, // elsődleges szakma
        allProfessions: this.professions.map((x) => x.seoName).join(","),
        location: this.person.city.seoName, // település
        registrationDate: this.person.created,
        lastLogin: this.person.lastLogin,
        descWordCount: this.person.memo?.length
          ? this.person.memo.split(" ").length
          : 0, // leírás hossza szavakban
        descCharCount: this.person.memo?.length || 0, // leírás hossza karakterekben
        orders: this.person.winCount, // megbízások száma
        reviews: this.person.scores.length, // vélemények száma
        score: this.person.score / 2, // értékelés átlaga
        hasPhoto: this.$store.getters["professional/hasPicture"], // boolean -> 1 = van fotó, 0 = nincs fotó
      });
    },
    onRequestTender({ source }) {
      this.$track.analyticsEvent({
        category: "adatlap",
        action: "kattintas-ajanlatkeresre",
        label: source,
      });

      this.requestTender({
        person: this.person,
        profession: this.profession,
        city: this.city,
        source,
      });
    },
    onClickPhoneNumber() {
      this.$trackers.trackEvent({
        title: "Clicked on Phone Number on Profile",
        data: {
          personId: this.person?.id,
          personSeoName: this.person?.seoName,
        },
      });
      this.$track.customEvent({
        event: "initiate_call",
        ecommerce: { items: [this.person.id] },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@use "styles/_all-members" as global;

.memo {
  display: block;
  white-space: pre-wrap;
  overflow: auto;
}

.profile-body {
  width: 100%;
}

.card {
  position: relative;
  border-radius: 1rem;
  overflow: hidden;
  // Bulma's `.card` previously provided the white background + shadow.
  background-color: #fff;
  box-shadow: 0 0.5em 1em -0.125em rgba(10, 10, 10, 0.1),
    0 0 0 1px rgba(10, 10, 10, 0.02);
}

// .profile-body > .profile-section:first-of-type {
//   margin-top: 0rem;
// }

.profile-section {
  width: 100%;
  margin: 1.5rem 0;
}

.column > .profile-section:first-of-type {
  margin-top: 0;
}

.section-title {
  color: #2e72ad;
  margin-bottom: 0.8rem;
  font-weight: 700;
  font-size: 2rem;
}

.pandemic-sticker-wrapper {
  height: 70px;
  width: 100%;
  text-align: center;

  .pandemic-sticker {
    height: 100%;
  }
}

.sticky {
  margin: 1rem 0;
}

.sticky-mobile {
  display: none;

  @include global.mobile {
    display: block;
    position: sticky;
    bottom: 0;
    background: white;
    width: 100%;
    height: max-content;
    padding: 0.5rem 0.5rem 40px;
  }
}

.section-card {
  margin: 1rem 0;
  overflow: visible;
  width: 100%;
}

.limitedHeight {
  max-height: 400px;
  overflow: hidden;
}

.showMore {
  position: absolute;
  bottom: 0px;
  z-index: 2;
  left: 0px;
  padding: 3px 5px;
  padding-left: 15px;
  background: #ddd;
  color: #2e72ad;
  user-select: none;
  border-radius: 0 5px 0 0;

  &:hover {
    background: #2e72ad;
    color: white;
    cursor: pointer;
  }
}

.section-subtext {
  display: flex;
  justify-content: center;
  align-items: center;
}

.sticky-col {
  position: sticky;

  @include global.mobile {
    position: relative;
  }

  top: 1rem;
  margin-top: -1rem;
}

.profile-interaction {
  margin-top: 2rem;
}

.profession-wincount {
  font-size: 0.7rem;
  margin-left: -0.3rem;
  color: #74b8e6;
}
</style>
